import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, KeyRound, ArrowLeft } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/FloralBg";

export default function SignIn() {
  const [mode, setMode] = useState("signin"); // signin | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || "coder"}!`);
      const r = data.user.role;
      nav(r === "super_admin" ? "/superadmin" : r === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(apiErr(err, "Sign in failed"));
    } finally { setLoading(false); }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.email_configured === false) {
        toast.warning("Email delivery is not configured on the server yet. Please contact your administrator.", { duration: 8000 });
      } else {
        toast.success(data.message || "Verification code sent. Check your email inbox.");
      }
      // The code is NEVER shown on the website — it is delivered only to the user's email inbox.
      setMode("reset");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { email, code, new_password: newPw });
      toast.success(data.message);
      setMode("signin"); setPassword(""); setCode(""); setNewPw(""); setConfirmPw("");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="text-3xl" />
          <h1 className="serif text-5xl sm:text-6xl font-extrabold mt-6 glow-text">Welcome back</h1>
          <p className="text-muted-foreground mt-3 text-center text-sm max-w-xs">
            A premium space to learn, write and run code across languages.
          </p>
        </div>

        <div className="glass-strong rounded-3xl p-8">
          {mode === "signin" && (
            <form onSubmit={doLogin} className="space-y-5">
              <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail}
                     placeholder="you@domain.com" testid="signin-email" />
              <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword}
                     placeholder="••••••••" testid="signin-password" />
              <button data-testid="signin-submit" disabled={loading}
                      className="kiq-btn w-full py-3.5 flex items-center justify-center gap-2">
                {loading ? "Signing in…" : <>Sign In <ArrowRight size={18} /></>}
              </button>
              <button type="button" onClick={() => setMode("forgot")}
                      data-testid="forgot-password-link"
                      className="w-full text-center text-sm text-teal-accent hover:underline">
                Forgot password?
              </button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={sendCode} className="space-y-5">
              <p className="text-sm text-muted-foreground">Enter your email and we'll send a 6-digit verification code.</p>
              <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail}
                     placeholder="you@domain.com" testid="forgot-email" />
              <button data-testid="send-code-btn" disabled={loading}
                      className="kiq-btn w-full py-3.5 flex items-center justify-center gap-2">
                {loading ? "Sending…" : <>Send code <KeyRound size={18} /></>}
              </button>
              <BackLink onClick={() => setMode("signin")} />
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={doReset} className="space-y-5">
              <p className="text-sm text-muted-foreground">Enter the code sent to <b>{email}</b> and your new password.</p>
              <Field icon={KeyRound} label="Verification code" value={code} onChange={setCode}
                     placeholder="6-digit code" testid="reset-code" />
              <Field icon={Lock} label="New password" type="password" value={newPw} onChange={setNewPw}
                     placeholder="New password" testid="reset-new-password" />
              <Field icon={Lock} label="Confirm password" type="password" value={confirmPw} onChange={setConfirmPw}
                     placeholder="Confirm password" testid="reset-confirm-password" />
              <button data-testid="reset-submit" disabled={loading}
                      className="kiq-btn w-full py-3.5">{loading ? "Updating…" : "Update password"}</button>
              <BackLink onClick={() => setMode("signin")} />
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8 tracking-wide">
          Crafted by <span className="text-teal-accent">KIQ Techno Pvt Ltd</span>
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</label>
      <div className="kiq-input flex items-center gap-3 px-4 py-3">
        <Icon size={18} className="text-teal-accent shrink-0" />
        <input data-testid={testid} type={type} value={value} placeholder={placeholder}
               onChange={(e) => onChange(e.target.value)} required
               className="bg-transparent outline-none w-full text-sm placeholder:text-muted-foreground/60" />
      </div>
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button type="button" onClick={onClick}
            className="w-full text-center text-sm text-muted-foreground hover:text-teal-accent flex items-center justify-center gap-1">
      <ArrowLeft size={14} /> Back to sign in
    </button>
  );
}
